import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

interface SchemaField {
  children: SchemaField[]
  description: string | null
  name: string
  required: boolean
  type: string
}

interface SchemaFieldsResult {
  fields: SchemaField[]
  truncated: boolean
}

const maximumSchemaDepth = 5
const maximumSchemaFields = 200

function schemaRecord(value: unknown) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function resolveJsonPointer(root: Record<string, unknown>, pointer: string) {
  if (!pointer.startsWith('#/')) return null

  let current: unknown = root
  for (const segment of pointer
    .slice(2)
    .split('/')
    .map((part) => part.replaceAll('~1', '/').replaceAll('~0', '~'))) {
    const record = schemaRecord(current)
    if (!record || !(segment in record)) return null
    current = record[segment]
  }
  return schemaRecord(current)
}

function resolvedSchema(schema: Record<string, unknown>, root: Record<string, unknown>) {
  const reference = typeof schema.$ref === 'string' ? schema.$ref : null
  const target = reference ? resolveJsonPointer(root, reference) : null
  if (!target) return schema

  const overrides = Object.fromEntries(Object.entries(schema).filter(([key]) => key !== '$ref'))
  return { ...target, ...overrides }
}

function schemaType(
  schema: Record<string, unknown>,
  root: Record<string, unknown>,
  depth = 0,
): string {
  const resolved = resolvedSchema(schema, root)
  const directType = resolved.type
  const types =
    typeof directType === 'string'
      ? [directType]
      : Array.isArray(directType)
        ? directType.filter((item): item is string => typeof item === 'string')
        : []

  if (types.length > 0) {
    return types
      .map((type) => {
        if (type !== 'array') return type
        const items = schemaRecord(resolved.items)
        if (!items) return type
        const itemType =
          depth >= maximumSchemaDepth ? 'unknown' : schemaType(items, root, depth + 1)
        return `array<${itemType}>`
      })
      .join(' | ')
  }

  const variants = [resolved.anyOf, resolved.oneOf]
    .filter(Array.isArray)
    .flat()
    .map(schemaRecord)
    .filter((variant): variant is Record<string, unknown> => variant !== null)
  if (variants.length > 0) {
    if (depth >= maximumSchemaDepth) return 'unknown'
    return Array.from(
      new Set(variants.map((variant) => schemaType(variant, root, depth + 1))),
    ).join(' | ')
  }

  if (schemaRecord(resolved.properties)) return 'object'
  if (resolved.items) return 'array'
  return 'unknown'
}

function nestedObjectSchema(
  schema: Record<string, unknown>,
  root: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> | null {
  const resolved = resolvedSchema(schema, root)
  if (schemaRecord(resolved.properties)) return resolved

  const items = schemaRecord(resolved.items)
  if (items) {
    const resolvedItems = resolvedSchema(items, root)
    if (schemaRecord(resolvedItems.properties)) return resolvedItems
  }

  const variants = [resolved.anyOf, resolved.oneOf]
    .filter(Array.isArray)
    .flat()
    .map(schemaRecord)
    .filter((variant): variant is Record<string, unknown> => variant !== null)
  if (depth >= maximumSchemaDepth) return null
  for (const variant of variants) {
    const nested = nestedObjectSchema(variant, root, depth + 1)
    if (nested) return nested
  }
  return null
}

function schemaFields(schema: Record<string, unknown>, rootLabel: string): SchemaFieldsResult {
  const root = schema
  let fieldCount = 0
  let truncated = false

  const buildProperties = (parent: Record<string, unknown>, depth: number): SchemaField[] => {
    const resolvedParent = resolvedSchema(parent, root)
    const properties = schemaRecord(resolvedParent.properties)
    if (!properties) return []

    const required = new Set(
      Array.isArray(resolvedParent.required)
        ? resolvedParent.required.filter((name): name is string => typeof name === 'string')
        : [],
    )
    const fields: SchemaField[] = []
    const nestedFields: {
      field: SchemaField
      schema: Record<string, unknown>
    }[] = []

    for (const [name, value] of Object.entries(properties)) {
      if (fieldCount >= maximumSchemaFields) {
        truncated = true
        break
      }
      fieldCount += 1

      const child = schemaRecord(value) ?? {}
      const resolvedChild = resolvedSchema(child, root)
      const nested = nestedObjectSchema(resolvedChild, root)
      const field: SchemaField = {
        children: [],
        description:
          typeof resolvedChild.description === 'string' ? resolvedChild.description : null,
        name,
        required: required.has(name),
        type: schemaType(resolvedChild, root),
      }
      fields.push(field)
      if (nested) nestedFields.push({ field, schema: nested })
    }

    for (const { field, schema: nested } of nestedFields) {
      if (depth >= maximumSchemaDepth) {
        truncated = true
        continue
      }
      field.children = buildProperties(nested, depth + 1)
    }
    return fields
  }

  const resolvedRoot = resolvedSchema(schema, root)
  const rootProperties = schemaRecord(resolvedRoot.properties)
  let fields: SchemaField[]
  if (rootProperties || resolvedRoot.type === 'object') {
    fields = buildProperties(resolvedRoot, 0)
  } else {
    fieldCount += 1
    const nested = nestedObjectSchema(resolvedRoot, root)
    fields = [
      {
        children: nested ? buildProperties(nested, 1) : [],
        description: typeof resolvedRoot.description === 'string' ? resolvedRoot.description : null,
        name: rootLabel,
        required: true,
        type: schemaType(resolvedRoot, root),
      },
    ]
  }

  return { fields, truncated }
}

function formattedJson(value: unknown) {
  return JSON.stringify(value, null, 2)
}

function SectionChevronIcon({ expanded }: { expanded?: boolean }) {
  return (
    <svg
      aria-hidden
      className={cn(
        'size-4 shrink-0 text-[var(--color-text-muted)] transition-transform',
        expanded === undefined ? 'group-open:rotate-90' : expanded && 'rotate-90',
      )}
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="m6 3.5 4.5 4.5L6 12.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function SchemaSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <details className="group mt-5" open>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 marker:hidden [&::-webkit-details-marker]:hidden">
        <h4 className="text-xs font-semibold tracking-wide text-[var(--color-text)] uppercase">
          {title}
        </h4>
        <SectionChevronIcon />
      </summary>
      {children}
    </details>
  )
}

function SchemaFieldList({
  fields,
  requiredLabel,
  requiredTone,
  truncated,
}: {
  fields: SchemaField[]
  requiredLabel: string
  requiredTone: 'danger' | 'neutral'
  truncated: boolean
}) {
  const { t } = useTranslation('adminAgentTools')

  return (
    <>
      <div className="mt-2 divide-y divide-[var(--color-divider)] border-y border-[var(--color-divider)]">
        {fields.map((field, index) => (
          <SchemaFieldItem
            field={field}
            key={`${field.name}:${String(index)}`}
            requiredLabel={requiredLabel}
            requiredTone={requiredTone}
          />
        ))}
      </div>
      {truncated ? (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t('connections.tools.schemaLimitNotice')}
        </p>
      ) : null}
    </>
  )
}

function SchemaFieldContent({
  field,
  requiredLabel,
  requiredTone,
}: {
  field: SchemaField
  requiredLabel: string
  requiredTone: 'danger' | 'neutral'
}) {
  const { t } = useTranslation('adminAgentTools')

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-1.5 text-xs">
        <code className="font-semibold text-[var(--color-text)]">{field.name}</code>
        <span className="text-[var(--color-text-muted)]">({field.type})</span>
        {field.required ? (
          <span
            className={cn(
              requiredTone === 'danger'
                ? 'text-[var(--color-danger)]'
                : 'text-[var(--color-text-muted)]',
            )}
          >
            {requiredLabel}
          </span>
        ) : null}
        {field.children.length > 0 ? (
          <span className="text-[var(--color-text-subtle)]">
            {t('connections.tools.nestedFieldCount', { count: field.children.length })}
          </span>
        ) : null}
      </div>
      {field.description ? (
        <p className="mt-1 text-xs leading-[18px] text-[var(--color-text-muted)]">
          {field.description}
        </p>
      ) : null}
    </div>
  )
}

function SchemaFieldItem({
  field,
  requiredLabel,
  requiredTone,
}: {
  field: SchemaField
  requiredLabel: string
  requiredTone: 'danger' | 'neutral'
}) {
  const [expanded, setExpanded] = useState(false)
  const content = (
    <SchemaFieldContent field={field} requiredLabel={requiredLabel} requiredTone={requiredTone} />
  )

  return (
    <div>
      {field.children.length > 0 ? (
        <button
          aria-expanded={expanded}
          className="flex w-full cursor-pointer items-start justify-between gap-2 py-3 text-left"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          {content}
          <SectionChevronIcon expanded={expanded} />
        </button>
      ) : (
        <div className="py-3">{content}</div>
      )}

      {expanded ? (
        <div className="ml-3 border-t border-l border-[var(--color-divider)] pl-2">
          <div className="divide-y divide-[var(--color-divider)]">
            {field.children.map((child, index) => (
              <SchemaFieldItem
                field={child}
                key={`${child.name}:${String(index)}`}
                requiredLabel={requiredLabel}
                requiredTone={requiredTone}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function ToolSchemaDetails({
  inputSchema,
  outputSchema,
}: {
  inputSchema: Record<string, unknown> | null
  outputSchema: Record<string, unknown> | null
}) {
  const { t } = useTranslation('adminAgentTools')
  const input = inputSchema
    ? schemaFields(inputSchema, t('connections.tools.inputValue'))
    : { fields: [], truncated: false }
  const output = outputSchema
    ? schemaFields(outputSchema, t('connections.tools.returnValue'))
    : null
  const outputType = outputSchema ? schemaType(outputSchema, outputSchema) : null

  return (
    <>
      <SchemaSection title={t('connections.tools.inputParameters')}>
        {input.fields.length > 0 ? (
          <SchemaFieldList
            fields={input.fields}
            requiredLabel={t('connections.tools.required')}
            requiredTone="danger"
            truncated={input.truncated}
          />
        ) : (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('connections.tools.noParameters')}
          </p>
        )}
      </SchemaSection>

      <SchemaSection title={t('connections.tools.outputParameters')}>
        {output === null ? (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('connections.tools.noStructuredOutput')}
          </p>
        ) : output.fields.length > 0 ? (
          <SchemaFieldList
            fields={output.fields}
            requiredLabel={t('connections.tools.outputRequired')}
            requiredTone="neutral"
            truncated={output.truncated}
          />
        ) : (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('connections.tools.noOutputFields', {
              type: outputType,
            })}
          </p>
        )}
      </SchemaSection>

      {inputSchema || outputSchema ? (
        <details className="mt-5 border-t border-[var(--color-divider)] pt-4">
          <summary className="cursor-pointer text-xs font-semibold text-[var(--color-text)]">
            {t('connections.tools.technicalDetails')}
          </summary>
          <div className="mt-3 grid gap-4">
            {inputSchema ? (
              <div>
                <p className="text-xs font-semibold text-[var(--color-text)]">
                  {t('connections.tools.inputSchema')}
                </p>
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-[var(--color-surface-muted)] p-3 text-[11px] leading-[17px] text-[var(--color-text)]">
                  {formattedJson(inputSchema)}
                </pre>
              </div>
            ) : null}
            {outputSchema ? (
              <div>
                <p className="text-xs font-semibold text-[var(--color-text)]">
                  {t('connections.tools.outputSchema')}
                </p>
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-[var(--color-surface-muted)] p-3 text-[11px] leading-[17px] text-[var(--color-text)]">
                  {formattedJson(outputSchema)}
                </pre>
              </div>
            ) : null}
          </div>
        </details>
      ) : null}
    </>
  )
}
